import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { frisokarLines } from '@/mocks/frisokar';
import { useSEO } from '@/hooks/useSEO';

export default function FrisokarPage() {
  const [activeTab, setActiveTab] = useState(frisokarLines[0].id);

  useSEO({
    title: 'Frisokar Caruaru PE | Revendedor Autorizado Cadeiras Ergonômicas NR17',
    description: 'Revendedor autorizado Frisokar em Caruaru PE. Cadeiras ergonômicas NR17, poltronas e mobiliário corporativo com garantia de 5 anos. Entrega e suporte técnico em Caruaru e todo o Agreste PE.',
    keywords: 'Frisokar Recife, revendedor Frisokar Petrolina, cadeiras Frisokar Garanhuns, cadeiras ergonômicas NR17 Arcoverde, Frisokar Diretor Sertão PE, Frisokar Executivo Pernambuco, cadeiras escritório Agreste, Frisokar Nordeste, cadeiras Frisokar Recife preço, Frisokar Caruaru, Frisokar Bezerros, cadeiras ergonômicas Frisokar Gravatá, Frisokar Santa Cruz do Capibaribe',
    path: '/frisokar',
    ogTitle: 'Frisokar Caruaru PE | Revendedor Autorizado – Home Office Premium',
    ogDescription: 'Revendedor autorizado Frisokar em Caruaru PE. Cadeiras ergonômicas NR17 com garantia de 5 anos. Entrega e suporte técnico local.',
  });

  useEffect(() => {
    const schemaId = 'schema-frisokar';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Frisokar Caruaru PE – Home Office Premium',
      description: 'Revendedor autorizado Frisokar em Caruaru PE. Cadeiras ergonômicas, poltronas e mobiliário corporativo com certificação NR17.',
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/frisokar`,
      about: {
        '@type': 'Brand',
        name: 'Frisokar',
        description: 'Referência nacional em cadeiras ergonômicas com mais de 30 anos de mercado. Certificação NR17.',
      },
    });
    const breadcrumbId = 'schema-frisokar-breadcrumb';
    let breadcrumbScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!breadcrumbScript) { breadcrumbScript = document.createElement('script'); breadcrumbScript.id = breadcrumbId; breadcrumbScript.type = 'application/ld+json'; document.head.appendChild(breadcrumbScript); }
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? '' },
        { '@type': 'ListItem', position: 2, name: 'Marcas', item: `${import.meta.env.VITE_SITE_URL ?? ''}/marcas` },
        { '@type': 'ListItem', position: 3, name: 'Frisokar', item: `${import.meta.env.VITE_SITE_URL ?? ''}/frisokar` },
      ],
    });
    return () => { script?.remove(); breadcrumbScript?.remove(); };
  }, []);

  const activeLine = frisokarLines.find(l => l.id === activeTab);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 h-[520px] md:h-[600px] overflow-hidden bg-gray-900">
        <img
          src="https://www.frisokar.com.br/wp-content/uploads/2024/06/5175.jpg"
          alt="Frisokar"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <i className="ri-star-fill text-xs"></i>
                Marca Parceira Oficial
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                Frisokar
              </h1>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                Referência nacional em cadeiras ergonômicas e mobiliário corporativo. Mais de 30 anos de mercado com foco total no bem-estar do usuário.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.frisokar.com.br/produtos/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Ver Catálogo Completo
                </a>
                <a
                  href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#d4560a] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Solicitar Orçamento
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sobre */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Quem é a Frisokar</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Mais de 30 anos fabricando cadeiras ergonômicas de alto padrão
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                A Frisokar é uma marca do FK Grupo S.A., com sede em Bariri-SP. Referência nacional em cadeiras de escritório, poltronas, sofás corporativos e mobiliário colaborativo, a marca atende desde pequenos escritórios até grandes corporações.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                Somos revendedores autorizados da Frisokar em Caruaru e região, com toda a linha disponível para pronta entrega ou sob encomenda.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-shield-check-line', label: 'Certificação NR17' },
                  { icon: 'ri-truck-line', label: 'Entrega em todo Brasil' },
                  { icon: 'ri-tools-line', label: 'Suporte Técnico' },
                  { icon: 'ri-award-line', label: 'Revendedor Autorizado' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#E8610A]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#E8610A] text-sm`}></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '30+', label: 'Anos de Mercado' },
                { value: '186+', label: 'Modelos no Catálogo' },
                { value: 'NR17', label: 'Certificação Ergonômica' },
                { value: 'BR', label: 'Fabricação Nacional' },
              ].map(stat => (
                <div key={stat.label} className="bg-white rounded-2xl p-6 text-center border border-orange-100">
                  <p className="text-3xl font-bold text-[#E8610A] mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Linhas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Produtos Frisokar</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              Selecione um produto para ver detalhes. Clique em "Ver no site oficial" para explorar todos os modelos e configurações.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {frisokarLines.map(line => (
              <button
                key={line.id}
                onClick={() => setActiveTab(line.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === line.id
                    ? 'bg-[#E8610A] text-white'
                    : 'bg-orange-50 text-gray-600 hover:bg-orange-100'
                }`}
              >
                {line.name}
              </button>
            ))}
          </div>

          {activeLine && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {/* Fotos */}
              <div className={`grid gap-4 ${activeLine.photos.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {activeLine.photos.map((photo, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl overflow-hidden bg-[#f8f8f6] flex items-center justify-center p-4 ${
                      activeLine.photos.length === 1 ? 'h-[480px]' : 'h-64'
                    }`}
                  >
                    <img
                      src={photo}
                      alt={`${activeLine.name} ${idx + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ))}
              </div>

              {/* Info */}
              <div>
                <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-3">{activeLine.category}</p>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{activeLine.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">{activeLine.description}</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={activeLine.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#d4560a] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-external-link-line"></i>
                    Ver no site Frisokar
                  </a>
                  <a
                    href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-orange-50 text-gray-700 text-sm font-medium px-6 py-3 rounded-full hover:bg-orange-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line"></i>
                    Pedir orçamento
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-4">Revendedor Autorizado</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Quer equipar seu escritório com<br className="hidden md:block" /> cadeiras Frisokar?
          </h2>
          <p className="text-gray-400 text-sm mb-10 max-w-lg mx-auto">
            Fale com nossa equipe e receba um orçamento personalizado. Atendemos empresas, escritórios e espaços corporativos em Caruaru e região.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-[#d4560a] transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line"></i>
              Falar no WhatsApp
            </a>
            <a href="https://www.frisokar.com.br/produtos/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-external-link-line"></i>
              Ver Catálogo Oficial
            </a>
            <Link to="/contato"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-mail-line"></i>
              Enviar Mensagem
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
